/**
 * Macro resolution engine.
 *
 * @module macros/resolver
 *
 * Pure utility functions for detecting, extracting, and resolving macro
 * expressions in strings. No Effect dependency.
 */

import { MACRO_PATTERN } from '@chatsdet/types';
import type { MacroResolutionContext, MacroResolutionLog } from './types.js';
import { MacroUnknownError } from './types.js';
import { getMacro } from './registry.js';

/** Create a fresh resolution context for a new workflow run. */
export function createMacroContext(): MacroResolutionContext {
  return { sequenceCounters: new Map(), aiResolutions: new Map() };
}

/** Fast check: does the value contain at least one macro expression? */
export function containsMacro(value: string): boolean {
  // Reset lastIndex since MACRO_PATTERN has the global flag
  MACRO_PATTERN.lastIndex = 0;
  return MACRO_PATTERN.test(value);
}

/** Extract all macro expressions from a string (e.g. ["$UUID()", "$EMAIL()"]). */
export function extractMacroExpressions(value: string): string[] {
  MACRO_PATTERN.lastIndex = 0;
  const results: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = MACRO_PATTERN.exec(value)) !== null) {
    results.push(match[0]);
  }
  return results;
}

/** Extract unique, normalized `$AI(prompt)` prompts from one or more values. */
export function extractAIMacroPrompts(values: Iterable<string>): string[] {
  const prompts = new Set<string>();

  for (const value of values) {
    const pattern = new RegExp(MACRO_PATTERN.source, MACRO_PATTERN.flags);
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(value)) !== null) {
      if (match[1] !== 'AI') continue;
      const prompt = (match[2] ?? '').trim().replace(/^["']|["']$/g, '');
      if (prompt) prompts.add(prompt);
    }
  }

  return [...prompts];
}

/**
 * Resolve all macro expressions in a string, returning the result with
 * macros replaced by their generated values.
 *
 * Escaped macros (\$MACRO()) are preserved as literal $MACRO().
 *
 * @throws MacroUnknownError if an unrecognized macro name is encountered
 * @throws MacroArgError if macro arguments are invalid
 */
export function resolveMacros(
  value: string,
  ctx: MacroResolutionContext,
  onResolved?: (occurrence: { expression: string; name: string; resolvedValue: string }) => void
): string {
  MACRO_PATTERN.lastIndex = 0;
  const result = value.replace(MACRO_PATTERN, (fullMatch, name: string, argsStr: string) => {
    const macroFn = getMacro(name);
    if (!macroFn) {
      throw new MacroUnknownError(name);
    }
    const args = argsStr.length > 0 ? argsStr.split(',') : [];
    const resolvedValue = macroFn(args, ctx);
    onResolved?.({ expression: fullMatch, name, resolvedValue });
    return resolvedValue;
  });

  // Unescape \$ → $
  return result.replace(/\\\$/g, '$');
}

/**
 * Resolve macros in all values of a Record<string, string>.
 * Returns the resolved map and a log of what was resolved.
 */
export function resolveMacrosInRecord(
  values: Record<string, string>,
  ctx: MacroResolutionContext
): {
  resolved: Record<string, string>;
  log: Map<string, { expression: string; resolved: string }>;
} {
  const resolved: Record<string, string> = {};
  const log = new Map<string, { expression: string; resolved: string }>();

  for (const [key, value] of Object.entries(values)) {
    if (containsMacro(value)) {
      const resolvedValue = resolveMacros(value, ctx);
      resolved[key] = resolvedValue;
      log.set(key, { expression: value, resolved: resolvedValue });
    } else {
      resolved[key] = value;
    }
  }

  return { resolved, log };
}

/**
 * Resolve macros in a node's inputValues and update workflowState.
 * Returns the resolved inputValues and a resolution log.
 */
export function resolveNodeInputMacros(
  inputValues: Record<string, string> | undefined,
  ctx: MacroResolutionContext,
  workflowState: Record<string, unknown>
): {
  resolvedValues: Record<string, string> | undefined;
  macroResolutionLog: MacroResolutionLog | undefined;
} {
  if (!inputValues) return { resolvedValues: undefined, macroResolutionLog: undefined };

  const hasMacros = Object.values(inputValues).some((v) => containsMacro(v));
  if (!hasMacros) return { resolvedValues: inputValues, macroResolutionLog: undefined };

  const { resolved, log } = resolveMacrosInRecord(inputValues, ctx);
  for (const [key, { resolved: val }] of log.entries()) {
    workflowState[`macro:${key}`] = val;
  }
  return { resolvedValues: resolved, macroResolutionLog: log };
}
